import { useEffect, useId } from "react";
import { ArrowUp, Check, ChevronDown, Plus, Sparkles } from "lucide-react";
import { easeOut, progress } from "./demoScenario";
import { useDemoPlayback } from "./useDemoPlayback";
import "./ai-chat-demo.css";

const CHATS = {
  claude: {
    name: "Claude",
    question: "What do I keep missing in my diet?",
    tool: "Checking 14 days in Nomsy",
    intro: "Two things stand out in your food log:",
    findings: [
      ["Fiber", "Below your target on 10 of 14 days."],
      ["Calcium", "Below your target on 9 of 14 days."],
    ],
    advice: "Try oats with yogurt and berries at breakfast. They add fiber and calcium.",
    followUp: "Find nearby restaurants that fit your needs?",
    description: "Illustrative Claude conversation: Nomsy supplies two weeks of logged meals. Claude identifies recurring low fiber and calcium, then suggests a breakfast change.",
  },
  chatgpt: {
    name: "ChatGPT",
    question: "Which of my usual breakfasts work best for me?",
    tool: "Comparing breakfasts in Nomsy",
    intro: "Your usual breakfasts, compared:",
    findings: [
      ["Yogurt, oats & berries", "24 g protein · 8 g fiber"],
      ["Toast & jam", "5 g protein · 2 g fiber"],
    ],
    advice: "The yogurt bowl helps with your protein and fiber gaps.",
    followUp: "Should I make a shopping list?",
    description: "Illustrative ChatGPT conversation: using breakfast history from Nomsy, ChatGPT compares protein and fiber and recommends the yogurt, oats and berries breakfast.",
  },
} as const;

export function AiChatDemo({ assistant }: { assistant: keyof typeof CHATS }) {
  const chat = CHATS[assistant];
  const descriptionId = useId();
  const player = useDemoPlayback(18000);
  const { setImageReady } = player;
  useEffect(() => { setImageReady(true); }, [setImageReady]);
  const time = player.reducedMotion ? 16000 : player.time;
  const submitted = time >= 3200;
  const answered = time >= 5600;
  const typed = chat.question.slice(0, Math.floor(progress(time, 450, 2300) * chat.question.length));
  const appear = (start: number) => {
    const value = player.reducedMotion ? 1 : easeOut(progress(time, start, 550));
    return { opacity: value, transform: `translateY(${(1 - value) * 5}px)` };
  };
  return <div ref={player.stageRef} className={`nc-demo nc-${assistant}`} role="region" aria-label={`${chat.name} with Nomsy animation`} aria-describedby={descriptionId} data-playing={player.running} data-chat-phase={answered ? "answer" : submitted ? "checking" : "typing"}>
    <p id={descriptionId} className="sr-only">{chat.description}</p>
    <div className="nc-window" aria-hidden="true">
      <div className="nc-titlebar"><span className="nc-traffic"><i /><i /><i /></span><span>{chat.name}</span><span className="nc-titlebar-spacer" /></div>
      <div className="nc-conversation" style={{ opacity: player.reducedMotion ? 1 : 1 - progress(time, 17500, 500) }}>
        <div className="nc-brand">{assistant === "claude" ? <span className="nc-claude-mark">✳</span> : <Sparkles />}<strong>{chat.name}</strong><ChevronDown /></div>
        {!submitted ? <div className="nc-greeting">{assistant === "claude" ? "What’s on your mind?" : "What can I help with?"}</div> : null}
        {submitted ? <div className="nc-question" style={appear(3200)}>{chat.question}</div> : null}
        {submitted ? <div className="nc-tool" style={appear(3700)}>{answered ? <Check /> : <span className="nc-tool-dot" style={{ opacity: .35 + Math.abs(Math.sin(time / 400)) * .65 }} />}<span>{answered ? "Used your Nomsy food log" : chat.tool}</span></div> : null}
        {answered ? <div className="nc-answer"><p style={appear(5600)}>{chat.intro}</p><div className="nc-findings">{chat.findings.map(([title, detail], i) => <div key={title} style={appear(6500 + i * 850)}><strong>{title}</strong><span>{detail}</span></div>)}</div><p className="nc-advice" style={appear(8500)}>{chat.advice}</p><p className="nc-follow-up" style={appear(10500)}>{chat.followUp}</p></div> : null}
      </div>
      <div className={`nc-composer ${submitted ? "nc-composer-sent" : ""}`}><span>{submitted ? "Reply…" : typed || "Ask about your food…"}</span><div><Plus /><span className="nc-connected">Nomsy MCP connected</span><span className="nc-send" style={{ transform: `scale(${1 - Math.sin(progress(time, 2900, 300) * Math.PI) * .13})` }}><ArrowUp /></span></div></div>
    </div>
  </div>;
}
